import React, { useMemo, useState } from 'react';
import { ArrowUpRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import FooterCTA from '../components/site/FooterCTA';
import Footer from '../components/site/Footer';
import { blogPosts, blogCategories } from '../data/mock';

const PAGE_SIZE = 6;

const Pill = ({ children }) => (
  <span
    className="inline-block px-4 py-1.5 rounded-md font-display uppercase tracking-wider text-sm md:text-base"
    style={{ background: '#2EF5FF', color: '#12367F' }}
  >
    {children}
  </span>
);

const BlogHero = () => (
  <section className="pt-28 md:pt-36 pb-12 md:pb-16" style={{ background: '#FFFFFF' }}>
    <div className="container-x px-6">
      <Pill>Blog</Pill>
      <h1
        className="mt-8 font-display uppercase text-[#12367F] text-balance max-w-6xl"
        style={{ fontSize: 'clamp(2.5rem, 8vw, 8.5rem)', lineHeight: 0.9 }}
      >
        The latest industry insights
        <br />
        and deep dives from Nasu Group
      </h1>
    </div>
  </section>
);

const Filters = ({ active, onChange }) => (
  <div className="container-x px-6 pb-10 md:pb-14">
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#12367F]/60 font-medium mr-1">
        Filter by:
      </span>
      {blogCategories.map((cat) => {
        const isActive = active === cat;
        return (
          <button
            key={cat}
            onClick={() => onChange(cat)}
            className={`px-5 py-2.5 rounded-xl border-2 text-sm md:text-base font-display uppercase tracking-wide transition-all ${
              isActive
                ? 'bg-[#12367F] text-white border-[#12367F]'
                : 'bg-white text-[#12367F] border-[#12367F]/20 hover:border-[#12367F]'
            }`}
          >
            {cat}
          </button>
        );
      })}
    </div>
  </div>
);

const BlogCard = ({ post }) => (
  <Link
    to={`/blog/${post.id}`}
    className="work-card group relative block rounded-3xl overflow-hidden bg-[#0f1410]"
    style={{ aspectRatio: '4 / 5' }}
  >
    <img
      src={post.image}
      alt={post.title}
      loading="lazy"
      className="work-img absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/85" />

    {/* Top: pill + read time */}
    <div className="absolute top-5 left-5 right-5 flex items-center gap-3 flex-wrap">
      <span
        className="inline-block px-3 py-1.5 rounded-md font-display uppercase tracking-wider text-xs md:text-sm"
        style={{ background: '#2EF5FF', color: '#12367F' }}
      >
        {post.category}
      </span>
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/95 text-[#12367F] font-display uppercase tracking-wider text-xs md:text-sm">
        <Clock className="h-3.5 w-3.5" />
        {post.readTime}
      </span>
    </div>

    {/* Bottom: title + excerpt + arrow */}
    <div className="absolute left-6 right-6 bottom-6 text-white">
      <p className="font-display uppercase text-2xl md:text-3xl lg:text-[2.4rem] leading-[0.95]">
        {post.title}
      </p>
      <p className="mt-4 text-white/85 text-sm md:text-base max-w-md">{post.excerpt}</p>
    </div>
    <div
      className="absolute bottom-5 right-5 h-12 w-12 md:h-14 md:w-14 grid place-items-center rounded-xl border-2 border-white/40 text-white group-hover:bg-[#2EF5FF] group-hover:text-[#12367F] group-hover:border-[#2EF5FF] transition-colors"
    >
      <ArrowUpRight className="h-5 w-5 md:h-6 md:w-6" />
    </div>
  </Link>
);

const Blog = () => {
  const [active, setActive] = useState('All');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    if (active === 'All') return blogPosts;
    return blogPosts.filter((p) => p.category === active);
  }, [active]);

  const handleChange = (cat) => {
    setActive(cat);
    setVisible(PAGE_SIZE);
  };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <main className="bg-white text-[#12367F]">
      <Navbar />
      <BlogHero />
      <Filters active={active} onChange={handleChange} />

      <section className="pb-20 md:pb-28" style={{ background: '#FFFFFF' }}>
        <div className="container-x px-6">
          {filtered.length === 0 ? (
            <div className="py-24 text-center text-[#12367F]/60">
              No articles in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {shown.map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </div>
          )}

          <div className="mt-14 md:mt-20 grid place-items-center">
            {hasMore ? (
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="h-28 w-28 md:h-32 md:w-32 rounded-full bg-[#12367F] text-[#2EF5FF] font-display uppercase tracking-wide grid place-items-center hover:scale-[1.04] transition-transform"
              >
                More
              </button>
            ) : (
              filtered.length > PAGE_SIZE && (
                <p className="text-sm text-[#12367F]/60 uppercase tracking-wider">You&rsquo;ve reached the end.</p>
              )
            )}
          </div>
        </div>
      </section>

      <FooterCTA />
      <Footer />
    </main>
  );
};

export default Blog;
